import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HiveTopBar from './HiveTopBar';
import BottomNav from './BottomNav';
import HiveSidebar from './HiveSidebar';
import WorkspaceIndicator from './WorkspaceIndicator';
import useIsMobile from './useIsMobile';
import { isShellRoute, isFullBleedRoute } from './navConfig';
import './shell.css';

/**
 * The Hive app shell. Renders responsive chrome around the routed content
 * (`children`, the <Switch> from Base). Mobile = top bar + bottom nav; desktop
 * = sidebar + slim top bar. When a legacy app is open, the content region goes
 * full-width and the top bar switches to a contextual back/title bar.
 */
export default function HiveShell({ userInfo, logout, children, onNavigate }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const appOpen = !isShellRoute(location.pathname);
  // A full-bleed app draws its own chrome: no Hive top bar, no sidebar, and a
  // transparent canvas so the app paints its own background in both themes.
  const fullBleed = isFullBleedRoute(location.pathname);

  // Re-pull permissions on every in-app navigation (throttled in Base), so the
  // menu, My Apps, and route access reflect grants/revokes without a reload.
  useEffect(() => {
    if (onNavigate) onNavigate();
  }, [location.pathname, onNavigate]);

  // Desktop left-menu collapse (persisted).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return localStorage.getItem('hive.sidebarClosed') !== '1';
    } catch (e) {
      return true;
    }
  });
  const toggleSidebar = () =>
    setSidebarOpen((o) => {
      const next = !o;
      try {
        localStorage.setItem('hive.sidebarClosed', next ? '0' : '1');
      } catch (e) {
        /* no-op */
      }
      return next;
    });

  const contentClass =
    `hive-content hive-scroll${appOpen ? ' hive-content-app' : ''}` +
    (fullBleed ? ' hive-content-full' : '');

  if (isMobile) {
    // Inside a legacy app, hide the bottom nav — its fixed bar overlaps the
    // apps' own full-screen dialogs / bottom sheets. Return to Hive via the
    // contextual top bar's back arrow. The nav (and its reserved space) only
    // shows on the marketplace shell screens.
    return (
      <div className="hive-root hive-app hive-app-mobile">
        {!fullBleed && <HiveTopBar userInfo={userInfo} logout={logout} isMobile />}
        {!appOpen && (
          <div className="hive-wsstrip-wrap hive-container">
            <WorkspaceIndicator userInfo={userInfo} variant="strip" />
          </div>
        )}
        <main className={`${contentClass}${appOpen ? '' : ' hive-content-mobile'}`}>{children}</main>
        {!appOpen && <BottomNav />}
      </div>
    );
  }

  return (
    <div className="hive-root hive-app">
      <div className="hive-layout">
        {sidebarOpen && !fullBleed && (
          <HiveSidebar userInfo={userInfo} logout={logout} onClose={toggleSidebar} />
        )}
        <div className="hive-main">
          {!fullBleed && (
            <HiveTopBar
              userInfo={userInfo}
              logout={logout}
              isMobile={false}
              onToggleSidebar={toggleSidebar}
            />
          )}
          <main className={contentClass}>{children}</main>
        </div>
      </div>
    </div>
  );
}
